import { cn } from "@istari-frontend/shared";
import { Button, ToggleGroup, ToggleGroupItem } from "@istari-frontend/ui";
import { ChevronsDownUp, ChevronsUpDown } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { StatusCard } from "~/components/status-card";
import { useParsedJson } from "~/components/use-parsed-json";
import { JSON_FILE_TREE_DISPLAY_MAX_SIZE, JSON_PRETTY_VIEW_MAX_SIZE } from "~/constants";
import { ThemedLoadingSpinner } from "~/theming/feature/themed-loading-spinner";
import { parseJsonPathSegments } from "~/utils/jsonpath";

import { type JsonValue } from "../json-tree-viewer.utils";
import { CopyContentButton } from "./copy-content-button";
import { FileProperties } from "./file-properties";
import { collectCollapsiblePaths, collectNestedCollapsiblePaths, JsonPrettyView } from "./json-pretty-view";
import { JsonViewMode } from "./json-view-mode";
import { SelectionAction } from "./selection-action";
import { TextFileViewer } from "./text-file-viewer";
import { ViewerPrefs } from "./url-fragment";
import type { ViewConfig } from "./view-config";

interface LineRange {
  startLine: number;
  endLine: number;
}

interface JsonViewerProps {
  content: string | Uint8Array;
  properties: FileProperties;
  versionId?: string;
  initialView?: ViewerPrefs;
  jsonViewMode?: JsonViewMode | null;
  viewConfig?: ViewConfig | null;
  lineRange?: LineRange | null;
  onSelectionChange?: (action: SelectionAction) => void;
}

const MAX_SECTIONS_OPEN_ON_LOAD = 200;

export function JsonViewer({
  content,
  properties,
  versionId,
  initialView,
  jsonViewMode,
  viewConfig,
  lineRange,
  onSelectionChange,
}: JsonViewerProps) {
  const [mode, setMode] = useState<JsonViewMode>(jsonViewMode ?? "pretty");
  const [userCollapsedPaths, setUserCollapsedPaths] = useState<{
    data: JsonValue | undefined;
    paths: ReadonlySet<string>;
  }>({ data: undefined, paths: new Set() });

  useEffect(() => {
    if (jsonViewMode) setMode(jsonViewMode);
  }, [jsonViewMode]);

  const fileSize = properties.size;
  const fitsInMemory = fileSize != null && fileSize <= JSON_FILE_TREE_DISPLAY_MAX_SIZE;
  // Collapsing sections bounds a deep document, but a flat one -- a single long array of scalars --
  // has no sections to collapse and would still mount every item. Past this size the tree view,
  // which virtualizes its rows, is the honest place to read it.
  const fitsPrettyView = fileSize != null && fileSize <= JSON_PRETTY_VIEW_MAX_SIZE;
  // A line range addresses raw text lines, which the pretty rendering has no equivalent for.
  const prettyRequested = mode === "pretty" && !lineRange && fitsInMemory && fitsPrettyView;
  const parsedJson = useParsedJson(content, prettyRequested);

  const prettyData = useMemo((): JsonValue | undefined => {
    if (!prettyRequested || parsedJson.status !== "success") return undefined;
    return applyViewConfigPath(parsedJson.value, viewConfig?.json?.path);
  }, [prettyRequested, parsedJson, viewConfig]);

  const collapsiblePaths = useMemo(
    () => (prettyData === undefined ? [] : collectCollapsiblePaths(prettyData)),
    [prettyData],
  );

  const nestedCollapsiblePaths = useMemo(
    () => (prettyData === undefined ? [] : collectNestedCollapsiblePaths(prettyData)),
    [prettyData],
  );

  // Every open section is mounted, so past this many a document opens at its top level and the
  // rest is one click away.
  const initialCollapsedPaths = useMemo(
    (): ReadonlySet<string> =>
      collapsiblePaths.length > MAX_SECTIONS_OPEN_ON_LOAD ? new Set(nestedCollapsiblePaths) : new Set(),
    [collapsiblePaths, nestedCollapsiblePaths],
  );

  const collapsedPaths = userCollapsedPaths.data === prettyData ? userCollapsedPaths.paths : initialCollapsedPaths;

  const setCollapsedPaths = useCallback(
    (paths: ReadonlySet<string>) => setUserCollapsedPaths({ data: prettyData, paths }),
    [prettyData],
  );

  const toggleCollapsed = useCallback(
    (path: string) => {
      const next = new Set(collapsedPaths);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      setCollapsedPaths(next);
    },
    [collapsedPaths, setCollapsedPaths],
  );

  const selectPath = useCallback(
    (path: string) => onSelectionChange?.({ type: "json-path", path }),
    [onSelectionChange],
  );

  const getCopyText = useCallback(
    () => (parsedJson.status === "success" ? JSON.stringify(parsedJson.value, null, 2) : ""),
    [parsedJson],
  );

  const prettyAvailable = prettyRequested && parsedJson.status !== "error";
  const toolbar = (
    <JsonViewerToolbar
      mode={mode}
      onModeChange={setMode}
      notice={
        mode === "pretty" && !prettyAvailable
          ? getPrettyUnavailableNotice(lineRange, fitsInMemory, fitsPrettyView)
          : null
      }
      sectionControls={prettyData !== undefined && collapsiblePaths.length > 0}
      onExpandAll={() => setCollapsedPaths(new Set())}
      onCollapseAll={() => setCollapsedPaths(new Set(collapsiblePaths))}
    />
  );

  if (!prettyAvailable) {
    return (
      <JsonViewerFrame toolbar={toolbar}>
        <TextFileViewer
          content={content}
          // The text viewer resolves JSON from the extension alone, and this viewer only renders
          // for a file already resolved as JSON -- including one that states no extension and was
          // resolved by name. Without this, tree mode is silently ignored for those and the
          // handoff notice promises a tree while plain text renders.
          properties={{ ...properties, extension: properties.extension || "json" }}
          versionId={versionId}
          initialView={initialView}
          // Between the two ceilings the file is still JSON the tree can virtualize, so that is
          // where it goes. Past the tree's own limit, or unparseable, or a line range -- which
          // addresses raw lines -- it reads as text.
          jsonViewMode={
            mode === "text" || lineRange || !fitsInMemory || parsedJson.status === "error" ? "text" : "tree"
          }
          viewConfig={viewConfig}
          lineRange={lineRange}
          onSelectionChange={onSelectionChange}
        />
      </JsonViewerFrame>
    );
  }

  if (prettyData === undefined) {
    return (
      <JsonViewerFrame toolbar={toolbar}>
        <StatusCard
          icon={<ThemedLoadingSpinner width="24px" height="24px" />}
          title="Loading file"
          description="Preparing a preview..."
        />
      </JsonViewerFrame>
    );
  }

  return (
    <JsonViewerFrame toolbar={toolbar} className="group">
      <CopyContentButton getText={getCopyText} />
      <div className="h-full overflow-auto">
        <JsonPrettyView
          data={prettyData}
          collapsedPaths={collapsedPaths}
          onToggleCollapsed={toggleCollapsed}
          selectedPath={initialView?.jsonPath}
          onSelectPath={selectPath}
        />
      </div>
    </JsonViewerFrame>
  );
}

const JSON_VIEW_MODE_OPTIONS: { value: JsonViewMode; label: string }[] = [
  { value: "pretty", label: "Pretty" },
  { value: "tree", label: "Tree" },
  { value: "text", label: "Text" },
];

function JsonViewerFrame({
  toolbar,
  className,
  children,
}: {
  toolbar: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      {toolbar}
      <div className={cn("relative min-h-0 flex-1", className)}>{children}</div>
    </div>
  );
}

function JsonViewerToolbar({
  mode,
  onModeChange,
  notice,
  sectionControls,
  onExpandAll,
  onCollapseAll,
}: {
  mode: JsonViewMode;
  onModeChange: (mode: JsonViewMode) => void;
  notice: string | null;
  sectionControls: boolean;
  onExpandAll: () => void;
  onCollapseAll: () => void;
}) {
  return (
    <div className="bg-background border-border flex items-center gap-2 border-b px-3 py-1.5">
      <ToggleGroup
        type="single"
        size="xs"
        variant="raised"
        aria-label="View mode"
        value={mode}
        onValueChange={(value) => {
          if (isJsonViewMode(value)) onModeChange(value);
        }}
      >
        {JSON_VIEW_MODE_OPTIONS.map((option) => (
          <ToggleGroupItem key={option.value} value={option.value}>
            {option.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
      {notice && <span className="text-muted-foreground truncate text-xs">{notice}</span>}
      {sectionControls && (
        <div className="ml-auto flex items-center gap-1">
          <Button variant="ghost" size="xs" tooltip="Expand all sections" onClick={onExpandAll}>
            <ChevronsUpDown />
            Expand all
          </Button>
          <Button variant="ghost" size="xs" tooltip="Collapse all sections" onClick={onCollapseAll}>
            <ChevronsDownUp />
            Collapse all
          </Button>
        </div>
      )}
    </div>
  );
}

function applyViewConfigPath(data: JsonValue, path: string | null | undefined): JsonValue {
  if (path == null || !path.startsWith("$")) return data;

  let current = data;
  for (const segment of parseJsonPathSegments(path)) {
    if (current === null || typeof current !== "object") return data;
    const next = Array.isArray(current) ? current[Number.parseInt(segment, 10)] : current[segment];
    if (next === undefined) return data;
    current = next;
  }
  return current;
}

function getPrettyUnavailableNotice(
  lineRange: LineRange | null | undefined,
  fitsInMemory: boolean,
  fitsPrettyView: boolean,
): string {
  if (lineRange) return "Line ranges are shown as raw text";
  if (!fitsInMemory) return "Too large for the pretty view";
  if (!fitsPrettyView) return "Too large for the pretty view — showing the tree";
  return "This file isn't valid JSON";
}

function isJsonViewMode(value: string): value is JsonViewMode {
  return value === "pretty" || value === "tree" || value === "text";
}
